import { useState, useEffect, useCallback, useRef } from 'react'
import ThemeCanvas from './ThemeCanvas'
import ThemeTransition from './Themetransition'
import { THEMES } from './themes'
import './App.css'

const API = `https://carefree-fulfillment-production-1b3f.up.railway.app/api`

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatBytes(b) {
  if (!b) return '—'
  if (b < 1024) return `${b} B`
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1048576).toFixed(2)} MB`
}
function formatTime(ms) {
  if (!ms || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}
function timeAgo(ts) {
  if (!ts) return '—'
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 5) return 'just now'
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  return new Date(ts).toLocaleTimeString()
}

const STATUS = {
  running:          { color: '#22d3ee', label: 'RUNNING',     pulse: true  },
  fetching_sitemap: { color: '#f59e0b', label: 'DISCOVERING', pulse: true  },
  completed:        { color: '#34d399', label: 'COMPLETED',   pulse: false },
  stopped:          { color: '#94a3b8', label: 'PAUSED',      pulse: false },
  failed:           { color: '#f87171', label: 'FAILED',      pulse: false },
  created:          { color: '#a78bfa', label: 'STARTING',    pulse: true  },
}

const TYPE_COLORS = {
  'img-tag': '#22d3ee', 'background-inline': '#a78bfa', 'css-style-tag': '#f59e0b',
  'css-file': '#fb923c', 'srcset': '#34d399', 'meta-tag': '#f472b6', 'svg-image': '#60a5fa',
}

// ── UI Atoms ──────────────────────────────────────────────────────────────────
function Pill({ status }) {
  const s = STATUS[status] || { color: '#888', label: status?.toUpperCase(), pulse: false }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}35`, borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', fontFamily: 'monospace' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0, animation: s.pulse ? 'blink 1.2s ease-in-out infinite' : 'none' }} />
      {s.label}
    </span>
  )
}

function Ring({ value, size = 60, stroke = 5, accent }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={accent} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={c - (value / 100) * c}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  )
}

function ThemePicker({ current, onChange, t }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
      <div style={{ fontSize: 9, color: t.muted, letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: 8 }}>THEME</div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {Object.entries(THEMES).map(([key, theme]) => (
          <button key={key} onClick={() => onChange(key)} title={theme.name}
            style={{ background: current === key ? `${theme.accent}20` : t.card, border: `1px solid ${current === key ? theme.accent : t.border}`, borderRadius: 8, padding: '5px 10px', color: current === key ? theme.accent : t.muted, cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 5, transform: current === key ? 'scale(1.05)' : 'scale(1)' }}>
            <span style={{ fontSize: 14 }}>{theme.icon}</span>{theme.name}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Image Crawler: Job Card ───────────────────────────────────────────────────
function JobCard({ job, selected, onSelect, onStop, onResume, onDelete, t }) {
  const elapsed = job.startedAt ? Date.now() - job.startedAt : 0
  const speed = elapsed > 0 ? Math.round((job.completedCount / (elapsed / 1000)) * 60) : 0
  return (
    <div onClick={() => onSelect(job.id)} className="job-card-anim"
      style={{ background: selected ? t.jobSelected : t.card, border: `1px solid ${selected ? t.jobSelectedBorder : t.border}`, borderRadius: 12, padding: '13px 14px', marginBottom: 8, cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)', boxShadow: selected ? `0 0 20px ${t.accent}18` : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.domain}</div>
          <Pill status={job.status} />
        </div>
        <div style={{ position: 'relative', flexShrink: 0, marginLeft: 8 }}>
          <Ring value={job.progress} accent={t.accent} />
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 11, fontWeight: 700, color: t.accent, fontFamily: 'monospace' }}>{job.progress}%</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, marginBottom: 10 }}>
        {[
          { v: job.completedCount, l: 'pages',  c: t.accent },
          { v: job.imageCount,     l: 'images', c: t.accent2 },
          { v: speed > 0 ? speed : '—', l: 'pg/min', c: t.accent3 },
          { v: job.errors || 0,    l: 'errors', c: '#f87171' },
        ].map(s => (
          <div key={s.l} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: s.c }}>{typeof s.v === 'number' ? s.v.toLocaleString() : s.v}</div>
            <div style={{ fontSize: 9, color: t.muted, letterSpacing: '0.06em' }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${job.progress}%`, background: t.progress, borderRadius: 2, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: t.muted }}>{timeAgo(job.createdAt)}</span>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          {(job.status === 'running' || job.status === 'fetching_sitemap') && (
            <button className="jbtn" style={{ color: '#f59e0b', border: `1px solid rgba(245,158,11,0.3)` }} onClick={() => onStop(job.id)}>⏸</button>
          )}
          {job.status === 'stopped' && (
            <button className="jbtn" style={{ color: t.accent2, border: `1px solid ${t.accent2}40` }} onClick={() => onResume(job.id)}>▶</button>
          )}
          {job.imageCount > 0 && (
            <a href={`${API}/jobs/${job.id}/csv`} className="jbtn" style={{ color: t.accent, border: `1px solid ${t.accent}40`, textDecoration: 'none' }} onClick={e => e.stopPropagation()}>↓</a>
          )}
          <button className="jbtn" style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }} onClick={() => onDelete(job.id)}>✕</button>
        </div>
      </div>
    </div>
  )
}

// ── Image Crawler: Live Feed ──────────────────────────────────────────────────
function LiveFeed({ jobId, jobStatus, t }) {
  const [images, setImages] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [extFilter, setExtFilter] = useState('')
  const [sortNewest, setSortNewest] = useState(true)
  const [loading, setLoading] = useState(false)
  const limit = 50

  const load = useCallback(async () => {
    if (!jobId) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit, search, type: typeFilter, ext: extFilter })
      const res = await fetch(`${API}/jobs/${jobId}/images?${params}`)
      const data = await res.json()
      let imgs = data.images || []
      if (sortNewest) imgs = [...imgs].reverse()
      setImages(imgs); setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }, [jobId, page, search, typeFilter, extFilter, sortNewest])

  useEffect(() => { setPage(1) }, [search, typeFilter, extFilter, jobId, sortNewest])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (jobStatus !== 'running' && jobStatus !== 'fetching_sitemap') return
    const i = setInterval(load, 3000); return () => clearInterval(i)
  }, [jobStatus, load])

  const totalPages = Math.ceil(total / limit)
  const iStyle = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 7, padding: '7px 12px', color: t.text, fontSize: 12, outline: 'none', fontFamily: 'monospace' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center', background: `${t.panel}cc`, backdropFilter: 'blur(8px)' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.muted, fontSize: 14, pointerEvents: 'none' }}>⌕</span>
          <input style={{ ...iStyle, paddingLeft: 30, width: '100%' }} placeholder="Search URLs…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={{ ...iStyle, minWidth: 130 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All Sources</option>
          <option value="img-tag">IMG Tag</option>
          <option value="background-inline">Background</option>
          <option value="css-style-tag">CSS Tag</option>
          <option value="css-file">CSS File</option>
          <option value="srcset">Srcset</option>
          <option value="meta-tag">Meta Tag</option>
        </select>
        <select style={{ ...iStyle, minWidth: 100 }} value={extFilter} onChange={e => setExtFilter(e.target.value)}>
          <option value="">All Ext</option>
          {['.jpg', '.jpeg', '.png', '.svg', '.webp', '.gif', '.ico', '.avif', 'unknown'].map(e => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <button style={{ ...iStyle, cursor: 'pointer', color: t.accent, borderColor: `${t.accent}40` }} onClick={() => setSortNewest(n => !n)}>
          {sortNewest ? '↓ Newest' : '↑ Oldest'}
        </button>
        <span style={{ fontSize: 11, color: t.muted, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{loading ? '…' : `${total.toLocaleString()} images`}</span>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr style={{ background: `${t.panel}ee`, backdropFilter: 'blur(8px)', borderBottom: `1px solid ${t.border}` }}>
              {['#', 'Page', 'Image', 'Source', 'Ext', 'Size', 'Time'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: 10, fontWeight: 700, color: t.muted, letterSpacing: '0.07em', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {images.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: t.muted, padding: 60, fontSize: 13 }}>
                {loading ? 'Loading…' : jobStatus === 'fetching_sitemap' ? '🔍 Discovering pages…' : 'Waiting for images…'}
              </td></tr>
            ) : images.map((img, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${t.border}20` }}
                onMouseEnter={e => e.currentTarget.style.background = `${t.accent}08`}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '7px 12px', color: t.muted, fontFamily: 'monospace', fontSize: 10 }}>{sortNewest ? total - ((page - 1) * limit) - i : (page - 1) * limit + i + 1}</td>
                <td style={{ padding: '7px 12px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <a href={img.pageUrl} target="_blank" rel="noreferrer" style={{ color: t.accent2, textDecoration: 'none', fontSize: 11 }}>
                    {img.pageUrl.replace(/^https?:\/\/[^/]+/, '') || '/'}
                  </a>
                </td>
                <td style={{ padding: '7px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 280 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 5, overflow: 'hidden', flexShrink: 0, background: t.card, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={img.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} loading="lazy" />
                    </div>
                    <a href={img.imageUrl} target="_blank" rel="noreferrer" style={{ color: t.muted, textDecoration: 'none', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                      {img.imageUrl.replace(/^https?:\/\//, '').slice(0, 55)}
                    </a>
                  </div>
                </td>
                <td style={{ padding: '7px 12px' }}>
                  <span style={{ background: `${TYPE_COLORS[img.type] || '#888'}18`, color: TYPE_COLORS[img.type] || '#888', border: `1px solid ${TYPE_COLORS[img.type] || '#888'}30`, borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{img.type}</span>
                </td>
                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontSize: 11, color: t.accent3 }}>{img.extension}</td>
                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontSize: 11, color: t.muted }}>{formatBytes(img.fileSize)}</td>
                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontSize: 10, color: t.muted, whiteSpace: 'nowrap' }}>{img.crawledAt ? new Date(img.crawledAt).toLocaleTimeString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, borderTop: `1px solid ${t.border}`, flexShrink: 0, background: `${t.panel}cc` }}>
          {[['«', 1], ['‹', page - 1]].map(([l, p]) => (
            <button key={l} onClick={() => setPage(p)} disabled={page === 1} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 6, padding: '5px 10px', color: t.muted, cursor: 'pointer', fontSize: 13, opacity: page === 1 ? .3 : 1 }}>{l}</button>
          ))}
          <span style={{ fontSize: 12, color: t.muted, padding: '0 8px', fontFamily: 'monospace' }}>Page <b style={{ color: t.text }}>{page}</b> / <b style={{ color: t.text }}>{totalPages}</b></span>
          {[['›', page + 1], ['»', totalPages]].map(([l, p]) => (
            <button key={l} onClick={() => setPage(p)} disabled={page === totalPages} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 6, padding: '5px 10px', color: t.muted, cursor: 'pointer', fontSize: 13, opacity: page === totalPages ? .3 : 1 }}>{l}</button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Image Crawler: Stats ──────────────────────────────────────────────────────
function StatsPanel({ jobId, t }) {
  const [stats, setStats] = useState(null)
  useEffect(() => {
    if (!jobId) return
    const load = () => fetch(`${API}/jobs/${jobId}/stats`).then(r => r.json()).then(setStats).catch(() => {})
    load(); const i = setInterval(load, 5000); return () => clearInterval(i)
  }, [jobId])

  if (!stats) return <div style={{ color: t.muted, padding: 40, textAlign: 'center' }}>Loading…</div>
  const COLS = [t.accent, t.accent2, t.accent3, '#f472b6', '#a78bfa', '#fb923c', '#34d399', '#f59e0b']

  return (
    <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { v: stats.totalImages.toLocaleString(), l: 'Total Images',    c: t.accent },
          { v: stats.totalPages.toLocaleString(),  l: 'Pages Crawled',   c: t.accent2 },
          { v: stats.avgImagesPerPage.toFixed(1),  l: 'Avg / Page',      c: t.accent3 },
          { v: formatBytes(stats.totalSizeBytes),  l: 'Total Asset Size', c: '#f472b6' },
        ].map((k, i) => (
          <div key={i} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 16, borderTop: `3px solid ${k.c}`, transition: 'transform 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
            <div style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 800, color: k.c, marginBottom: 4 }}>{k.v}</div>
            <div style={{ fontSize: 11, color: t.muted, letterSpacing: '0.06em' }}>{k.l}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[{ title: 'By Source Type', data: stats.byType }, { title: 'By Extension', data: stats.byExt }].map(({ title, data }) => (
          <div key={title} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 11, color: t.muted, letterSpacing: '0.1em', marginBottom: 14, fontFamily: 'monospace' }}>{title.toUpperCase()}</div>
            {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([key, count], i) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: COLS[i % COLS.length], width: 110, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flexShrink: 0, fontFamily: 'monospace' }}>{key}</div>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 3, width: `${(count / stats.totalImages * 100).toFixed(1)}%`, background: COLS[i % COLS.length], transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ fontSize: 11, color: t.muted, fontFamily: 'monospace', width: 50, textAlign: 'right' }}>{count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Image Crawler: Saved Data ─────────────────────────────────────────────────
function SavedData({ jobs, t, onSelectJob }) {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('date')
  let data = jobs.filter(j => j.imageCount > 0 && j.domain.toLowerCase().includes(search.toLowerCase()))
  if (sortBy === 'images') data = [...data].sort((a, b) => b.imageCount - a.imageCount)
  else if (sortBy === 'pages') data = [...data].sort((a, b) => b.completedCount - a.completedCount)
  else data = [...data].sort((a, b) => b.createdAt - a.createdAt)
  const iStyle = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 7, padding: '7px 12px', color: t.text, fontSize: 12, outline: 'none', fontFamily: 'monospace' }
  return (
    <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.muted, fontSize: 14, pointerEvents: 'none' }}>⌕</span>
          <input style={{ ...iStyle, paddingLeft: 30, width: '100%' }} placeholder="Search saved crawls…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={iStyle} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="date">Newest first</option>
          <option value="images">Most images</option>
          <option value="pages">Most pages</option>
        </select>
        <span style={{ fontSize: 12, color: t.muted, fontFamily: 'monospace' }}>{data.length} crawls</span>
      </div>
      {data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: t.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>📦</div>
          <div>No saved crawl data yet</div>
          <div style={{ fontSize: 12, marginTop: 6, opacity: 0.6 }}>Completed crawls with images appear here</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: 14 }}>
          {data.map(job => (
            <div key={job.id} onClick={() => onSelectJob(job.id)}
              style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${t.accent}20` }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 5 }}>{job.domain}</div>
                  <Pill status={job.status} />
                </div>
                <a href={`${API}/jobs/${job.id}/csv`} onClick={e => e.stopPropagation()}
                  style={{ background: `${t.accent}15`, border: `1px solid ${t.accent}40`, color: t.accent, borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>↓ CSV</a>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 12 }}>
                {[
                  { v: job.imageCount.toLocaleString(),     l: 'Images', c: t.accent },
                  { v: job.completedCount.toLocaleString(), l: 'Pages',  c: t.accent2 },
                  { v: job.errors || 0,                     l: 'Errors', c: job.errors > 0 ? '#f87171' : t.muted },
                ].map(s => (
                  <div key={s.l} style={{ background: t.bg || 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 800, color: s.c }}>{s.v}</div>
                    <div style={{ fontSize: 10, color: t.muted }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', width: `${job.progress}%`, background: t.progress, borderRadius: 2 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.muted, fontFamily: 'monospace' }}>
                <span>{new Date(job.createdAt).toLocaleDateString()} {new Date(job.createdAt).toLocaleTimeString()}</span>
                <span style={{ color: t.accent3 }}>{job.discoveryMethod || 'sitemap'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// PAGE DATA CRAWLER COMPONENTS
// ════════════════════════════════════════════════════════════════

const TAG_COLORS = {
  h1: '#f87171', h2: '#fb923c', h3: '#fbbf24', h4: '#a3e635', h5: '#86efac', h6: '#86efac',
  p: '#d4d4d8', a: '#60a5fa', img: '#c084fc', table: '#f472b6',
  ul: '#34d399', ol: '#34d399', li: '#6ee7b7', dl: '#34d399', dt: '#6ee7b7', dd: '#6ee7b7',
  div: '#71717a', span: '#71717a', section: '#7dd3fc', article: '#7dd3fc',
  nav: '#fda4af', header: '#fda4af', footer: '#c4b5fd', main: '#86efac', aside: '#fcd34d',
  form: '#fde68a', input: '#fde68a', button: '#fcd34d', select: '#fcd34d', textarea: '#fde68a',
  code: '#d1d5db', pre: '#d1d5db', blockquote: '#cbd5e1',
  figure: '#a78bfa', figcaption: '#c084fc',
  strong: '#f87171', em: '#fb923c', b: '#f87171', i: '#fb923c',
}
 
// ── Tag Tree Renderer ─────────────────────────────────────────────────────────
const TAG_PRIORITY = { h1: 10, h2: 9, h3: 8, h4: 7, h5: 6, h6: 5, p: 4, main: 9, article: 9, section: 7, header: 6, footer: 5, nav: 4, table: 6, ul: 5, ol: 5, form: 5 }


function TagNode({ node, depth = 0, t }) {
  const [open, setOpen] = useState(depth < 2)
  if (!node) return null
  const color = TAG_COLORS[node.tag] || '#71717a'
  const hasChildren = node.children && node.children.length > 0
  const priority = TAG_PRIORITY[node.tag] || 0
  const isHeading = ['h1','h2','h3','h4','h5','h6'].includes(node.tag)
  const indent = depth * 14
 
  return (
    <div style={{ marginLeft: indent }}>
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 5, padding: '3px 8px',
          borderRadius: 5, cursor: hasChildren ? 'pointer' : 'default',
          transition: 'background 0.1s',
          borderLeft: isHeading ? `2px solid ${color}40` : 'none',
          marginLeft: isHeading ? -2 : 0,
          background: isHeading ? `${color}06` : 'transparent',
        }}
        onClick={() => hasChildren && setOpen(o => !o)}
        onMouseEnter={e => e.currentTarget.style.background = `${color}10`}
        onMouseLeave={e => e.currentTarget.style.background = isHeading ? `${color}06` : 'transparent'}
      >
        {/* Toggle arrow */}
        <span style={{ color: t.muted, fontSize: 9, width: 10, flexShrink: 0, userSelect: 'none', paddingTop: 3, opacity: hasChildren ? 1 : 0 }}>
          {open ? '▾' : '▸'}
        </span>
 
        {/* Tag badge */}
        <span style={{
          background: `${color}20`, color, border: `1px solid ${color}35`,
          borderRadius: 4, padding: '1px 5px', fontSize: isHeading ? 12 : 10,
          fontFamily: 'monospace', fontWeight: isHeading ? 800 : 600, flexShrink: 0,
          minWidth: isHeading ? 28 : 'auto',
        }}>
          {node.tag}
        </span>
 
        {/* ID */}
        {node.id && <span style={{ fontSize: 10, color: '#60a5fa', fontFamily: 'monospace', flexShrink: 0 }}>#{node.id}</span>}
 
        {/* Classes - max 2 */}
        {node.classes?.length > 0 && (
          <span style={{ fontSize: 9, color: '#a78bfa', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160, flexShrink: 1 }}>
            .{node.classes.slice(0, 2).join('.')}
          </span>
        )}
 
        {/* Key attrs */}
        {node.attrs && Object.entries(node.attrs).slice(0, 2).map(([k, v]) => (
          <span key={k} style={{ fontSize: 9, color: t.muted, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140, flexShrink: 1 }}>
            <span style={{ color: '#fcd34d' }}>{k}</span>=<span style={{ color: '#86efac' }}>"{v.slice(0, 40)}"</span>
          </span>
        ))}
 
        {/* Text content */}
        {node.text && (
          <span style={{
            fontSize: isHeading ? 13 : 11,
            color: isHeading ? color : t.text,
            fontWeight: isHeading ? 700 : 400,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: 320, opacity: isHeading ? 1 : 0.75, flex: 1,
          }}>
            {node.text.slice(0, 100)}{node.text.length > 100 ? '…' : ''}
          </span>
        )}
 
        {/* Child count badge */}
        {hasChildren && !open && (
          <span style={{ fontSize: 9, color: t.muted, fontFamily: 'monospace', marginLeft: 'auto', flexShrink: 0, background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: '0 5px' }}>
            {node.children.length}
          </span>
        )}
      </div>
 
      {open && hasChildren && (
        <div>
          {node.children.map((child, i) => (
            <TagNode key={i} node={child} depth={depth + 1} t={t} />
          ))}
        </div>
      )}
    </div>
  )
}

function RichTextViewer({ content, t }) {
  if (!content) return <div style={{ color: t.muted, textAlign: 'center', padding: 40 }}>No content available</div>
 
  // Parse the rich text into styled segments
  const lines = content.split('\n')
  const elements = []
  let key = 0
 
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line.trim()) { elements.push(<div key={key++} style={{ height: 6 }} />); continue }
 
    // H1
    if (line.startsWith('■ H1:')) {
      elements.push(
        <div key={key++} style={{ margin: '20px 0 8px', padding: '10px 16px', background: `${t.accent}15`, borderLeft: `4px solid ${t.accent}`, borderRadius: '0 8px 8px 0' }}>
          <div style={{ fontSize: 9, color: t.accent, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 4 }}>H1</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: t.accent }}>{line.replace('■ H1:', '').trim()}</div>
        </div>
      )
    }
    // H2
    else if (line.startsWith('▶ H2:')) {
      elements.push(
        <div key={key++} style={{ margin: '16px 0 6px', padding: '8px 14px', background: `${t.accent2}12`, borderLeft: `3px solid ${t.accent2}`, borderRadius: '0 6px 6px 0' }}>
          <div style={{ fontSize: 9, color: t.accent2, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 3 }}>H2</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.accent2 }}>{line.replace('▶ H2:', '').trim()}</div>
        </div>
      )
    }
    // H3
    else if (line.startsWith('  ◆ H3:')) {
      elements.push(
        <div key={key++} style={{ margin: '12px 0 4px 12px', padding: '6px 12px', background: `${t.accent3}10`, borderLeft: `2px solid ${t.accent3}`, borderRadius: '0 4px 4px 0' }}>
          <div style={{ fontSize: 9, color: t.accent3, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 2 }}>H3</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.accent3 }}>{line.replace('  ◆ H3:', '').trim()}</div>
        </div>
      )
    }
    // H4
    else if (line.startsWith('    ● H4:')) {
      elements.push(
        <div key={key++} style={{ margin: '10px 0 3px 20px', padding: '4px 10px', borderLeft: `2px solid #f472b6`, color: '#f472b6' }}>
          <span style={{ fontSize: 9, fontFamily: 'monospace', marginRight: 6, opacity: 0.7 }}>H4</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{line.replace('    ● H4:', '').trim()}</span>
        </div>
      )
    }
    // H5/H6
    else if (line.match(/^\s+[○·] H[56]:/)) {
      const tag = line.match(/H([56]):/)?.[1]
      elements.push(
        <div key={key++} style={{ margin: '8px 0 2px 28px', color: t.muted, fontSize: 12, fontWeight: 600 }}>
          <span style={{ fontSize: 9, fontFamily: 'monospace', marginRight: 4, color: '#a78bfa' }}>H{tag}</span>
          {line.replace(/^\s+[○·] H[56]:/, '').trim()}
        </div>
      )
    }
    // Bullet list item
    else if (line.startsWith('    •')) {
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: 8, marginLeft: 16, marginBottom: 2 }}>
          <span style={{ color: t.accent2, flexShrink: 0, fontSize: 14 }}>•</span>
          <span style={{ fontSize: 13, color: t.text, lineHeight: 1.6 }}>{line.replace(/^\s+•\s*/, '')}</span>
        </div>
      )
    }
    // Numbered list
    else if (line.match(/^\s+\d+\.\s/)) {
      const [num, ...rest] = line.trim().split('. ')
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: 10, marginLeft: 20, marginBottom: 2 }}>
          <span style={{ color: t.accent, flexShrink: 0, fontFamily: 'monospace', fontSize: 12, minWidth: 20 }}>{num}.</span>
          <span style={{ fontSize: 13, color: t.text, lineHeight: 1.6 }}>{rest.join('. ')}</span>
        </div>
      )
    }
    // Image tag
    else if (line.startsWith('  [IMG:')) {
      const match = line.match(/\[IMG: (.+?) \| (.+?)\]/)
      elements.push(
        <div key={key++} style={{ margin: '6px 0', padding: '6px 10px', background: `${t.card}`, border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 11, color: t.muted, fontFamily: 'monospace', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#c084fc' }}>🖼</span>
          <span style={{ color: t.muted }}>alt: <span style={{ color: t.text }}>{match?.[1] || ''}</span></span>
          {match?.[2] && <span style={{ color: t.accent2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{match[2]}</span>}
        </div>
      )
    }
    // Table
    else if (line.startsWith('  [TABLE]')) {
      elements.push(<div key={key++} style={{ margin: '8px 0', padding: '4px 10px', background: `${t.card}`, border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 11, color: '#f472b6', fontFamily: 'monospace' }}>📋 TABLE</div>)
    }
    // Section markers
    else if (line.match(/^\s+\[(HEADER|FOOTER|NAV)\]/)) {
      const tag = line.match(/\[(\w+)\]/)?.[1]
      elements.push(<div key={key++} style={{ fontSize: 9, color: t.muted, fontFamily: 'monospace', letterSpacing: '0.1em', padding: '4px 8px', marginTop: 8 }}>[{tag}]</div>)
    }
    // Blockquote
    else if (line.includes('┃')) {
      elements.push(
        <div key={key++} style={{ margin: '6px 0', padding: '8px 12px', borderLeft: `3px solid ${t.accent3}`, background: `${t.accent3}08`, color: t.text, fontSize: 13, fontStyle: 'italic', lineHeight: 1.7 }}>
          {line.replace(/\s*┃\s*/, '')}
        </div>
      )
    }
    // Separator lines - skip
    else if (line.match(/^[━─·\s]+$/) && line.trim().length > 3) {
      continue
    }
    // Regular paragraph
    else if (line.trim().startsWith('  ') || line.trim().length > 0) {
      const text = line.trim()
      if (text.length > 0) {
        // Inline formatting: **bold**, _italic_, links
        const formatted = text
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/_(.+?)_/g, '<em>$1</em>')
          .replace(/`(.+?)`/g, `<code style="background:rgba(255,255,255,0.08);padding:1px 5px;border-radius:3px;font-family:monospace;font-size:11px">$1</code>`)
          .replace(/\[→(.+?)\]/g, `<span style="color:${t.accent2};font-size:10px;font-family:monospace"> ↗</span>`)
 
        elements.push(
          <p key={key++} style={{ margin: '4px 0', fontSize: 13, color: t.text, lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: formatted }} />
        )
      }
    }
  }
 
  return (
    <div style={{ maxWidth: 800, padding: '0 4px' }}>
      {elements}
    </div>
  )
}
 

// ── Body Content Viewer ───────────────────────────────────────────────────────
function BodyViewer({ body, t }) {
  const [mode, setMode] = useState('tree') // 'tree' | 'raw' | 'text' | 'rich'
  const [copiedKey, setCopiedKey] = useState(null)

  const doCopy = (text, key) => {
    navigator.clipboard?.writeText(text)
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const btnStyle = (active) => ({
    background: active ? `${t.accent}25` : t.card,
    border: `1px solid ${active ? t.accent : t.border}`,
    color: active ? t.accent : t.muted,
    borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
    transition: 'all 0.15s',
  })
 
  const copyBtnStyle = (active, color) => ({
    background: active ? `${color}35` : `${color}15`,
    border: `1px solid ${color}40`,
    color: active ? '#fff' : color,
    borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700,
    transition: 'all 0.3s',
  })

  const treeIsEmpty = !body?.tagTree || body.tagTree.length === 0
 
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', gap: 6, padding: '10px 16px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, alignItems: 'center', background: `${t.panel}aa`, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: t.muted, fontFamily: 'monospace', marginRight: 4 }}>VIEW AS</span>
        <button style={btnStyle(mode === 'tree')} onClick={() => setMode('tree')}>🌳 Tag Tree</button>
        <button style={btnStyle(mode === 'rich')} onClick={() => setMode('rich')}>📝 Formatted</button>
        <button style={btnStyle(mode === 'raw')} onClick={() => setMode('raw')}>{'</>'} Raw HTML</button>
        <button style={btnStyle(mode === 'text')} onClick={() => setMode('text')}>📄 Plain Text</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {mode === 'raw' && body?.html && (
            <button onClick={() => doCopy(body.html, 'html')}
              style={copyBtnStyle(copiedKey === 'html', t.accent2)}>
              {copiedKey === 'html' ? '✓ Copied!' : '⎘ Copy HTML'}
            </button>
          )}
          {(mode === 'text' || mode === 'rich') && (body?.richText || body?.text) && (
            <button onClick={() => doCopy(body.richText || body.text, 'text')}
              style={copyBtnStyle(copiedKey === 'text', t.accent2)}>
              {copiedKey === 'text' ? '✓ Copied!' : '⎘ Copy'}
            </button>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: mode === 'tree' ? '8px 4px' : 16 }}>
        {mode === 'tree' && (
          treeIsEmpty ? (
            <div style={{ color: t.muted, textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 32, opacity: 0.2, marginBottom: 8 }}>🌳</div>
              <div>No tag tree data</div>
              <div style={{ fontSize: 11, marginTop: 6, opacity: 0.6 }}>Try "Formatted" or "Raw HTML" view</div>
            </div>
          ) : body.tagTree.map((node, i) => <TagNode key={i} node={node} depth={0} t={t} />)
        )}
        {mode === 'rich' && <RichTextViewer content={body?.richText || body?.text} t={t} />}
        {mode === 'raw' && (
          <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, color: t.text, whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>
            {body?.html || 'No HTML available'}
          </pre>
        )}
        {mode === 'text' && (
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: t.text, lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {body?.text || 'No text content available'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Meta Viewer ───────────────────────────────────────────────────────────────
function MetaViewer({ meta, statusCode, t }) {
  if (!meta) return <div style={{ color: t.muted, padding: 40, textAlign: 'center' }}>No metadata</div>
  const COLS = [t.accent, t.accent2, t.accent3, '#f472b6', '#a78bfa']

  const Section = ({ title, children, color }) => (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16, marginBottom: 14, borderLeft: `3px solid ${color || t.accent}` }}>
      <div style={{ fontSize: 10, color: color || t.accent, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  )

  const Row = ({ label, value, accent }) => value ? (
    <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
      <div style={{ fontSize: 11, color: t.muted, fontFamily: 'monospace', width: 110, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 12, color: accent || t.text, flex: 1, wordBreak: 'break-all', lineHeight: 1.5 }}>{value}</div>
    </div>
  ) : null

  const statusColor = statusCode >= 200 && statusCode < 300 ? '#34d399' : statusCode >= 300 && statusCode < 400 ? '#f59e0b' : '#f87171'

  return (
    <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
      {/* Status + Core */}
      <Section title="CORE META" color={t.accent}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}40`, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>{statusCode || '—'}</span>
          <span style={{ fontSize: 11, color: t.muted }}>HTTP Status</span>
        </div>
        <Row label="title" value={meta.title} accent={t.accent} />
        <Row label="description" value={meta.description} />
        <Row label="keywords" value={meta.keywords} />
        <Row label="author" value={meta.author} />
        <Row label="canonical" value={meta.canonical} accent={t.accent2} />
        <Row label="lang" value={meta.lang} />
        <Row label="charset" value={meta.charset} />
        <Row label="viewport" value={meta.viewport} />
        <Row label="robots" value={meta.robots} />
        <Row label="theme-color" value={meta.themeColor} />
      </Section>

      {/* OG */}
      {meta.og && Object.keys(meta.og).length > 0 && (
        <Section title="OPEN GRAPH" color={t.accent2}>
          {Object.entries(meta.og).map(([k, v]) => <Row key={k} label={`og:${k}`} value={v} accent={k === 'image' ? '#f472b6' : undefined} />)}
        </Section>
      )}

      {/* Twitter */}
      {meta.twitter && Object.keys(meta.twitter).length > 0 && (
        <Section title="TWITTER CARD" color='#60a5fa'>
          {Object.entries(meta.twitter).map(([k, v]) => <Row key={k} label={`twitter:${k}`} value={v} />)}
        </Section>
      )}

      {/* Custom */}
      {meta.custom && meta.custom.length > 0 && (
        <Section title="CUSTOM META" color={t.accent3}>
          {meta.custom.map(({ name, content }) => <Row key={name} label={name} value={content} />)}
        </Section>
      )}

      {/* HTTP Equiv */}
      {meta.httpEquiv && Object.keys(meta.httpEquiv).length > 0 && (
        <Section title="HTTP-EQUIV" color='#a78bfa'>
          {Object.entries(meta.httpEquiv).map(([k, v]) => <Row key={k} label={k} value={v} />)}
        </Section>
      )}

      {/* Links */}
      {meta.links && meta.links.length > 0 && (
        <Section title="LINK TAGS" color='#fb923c'>
          {meta.links.map(({ rel, href }, i) => <Row key={i} label={rel} value={href} accent={t.accent2} />)}
        </Section>
      )}
    </div>
  )
}

// ── Schema Viewer ─────────────────────────────────────────────────────────────
function SchemaViewer({ schema, t }) {
  const [selected, setSelected] = useState(0)
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard?.writeText(JSON.stringify(schema[selected], null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  if (!schema || schema.length === 0) return (
    <div style={{ color: t.muted, textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 36, opacity: 0.2, marginBottom: 8 }}>∅</div>
      <div>No schema markup found on this page</div>
    </div>
  )

  const renderValue = (val, depth = 0) => {
    if (val === null || val === undefined) return <span style={{ color: t.muted }}>null</span>
    if (typeof val === 'string') return <span style={{ color: '#86efac' }}>"{val.slice(0, 300)}"</span>
    if (typeof val === 'number' || typeof val === 'boolean') return <span style={{ color: '#fcd34d' }}>{String(val)}</span>
    if (Array.isArray(val)) return (
      <span>
        <span style={{ color: t.muted }}>[</span>
        {val.map((item, i) => <span key={i}>{i > 0 && <span style={{ color: t.muted }}>, </span>}{renderValue(item, depth + 1)}</span>)}
        <span style={{ color: t.muted }}>]</span>
      </span>
    )
    if (typeof val === 'object') return (
      <div style={{ marginLeft: 16 }}>
        {Object.entries(val).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 4 }}>
            <span style={{ color: '#60a5fa', fontFamily: 'monospace', fontSize: 11 }}>"{k}"</span>
            <span style={{ color: t.muted }}>: </span>
            {renderValue(v, depth + 1)}
          </div>
        ))}
      </div>
    )
    return <span style={{ color: t.text }}>{String(val)}</span>
  }

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Schema list sidebar */}
      <div style={{ width: 200, borderRight: `1px solid ${t.border}`, overflowY: 'auto', flexShrink: 0 }}>
        {schema.map((s, i) => {
          const type = s['@type'] || s._source || 'Schema'
          const color = [t.accent, t.accent2, t.accent3, '#f472b6', '#a78bfa'][i % 5]
          return (
            <div key={i} onClick={() => setSelected(i)}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${t.border}20`, background: selected === i ? `${color}15` : 'transparent', borderLeft: `3px solid ${selected === i ? color : 'transparent'}`, transition: 'all 0.15s' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: selected === i ? color : t.text, fontFamily: 'monospace', marginBottom: 3 }}>{Array.isArray(type) ? type.join(', ') : type}</div>
              <div style={{ fontSize: 10, color: t.muted }}>{s._source || 'ld+json'}</div>
            </div>
          )
        })}
      </div>
      {/* Schema detail */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.text, fontFamily: 'monospace' }}>
            Schema {selected + 1} / {schema.length}
          </span>
          <button onClick={handleCopy}
            style={{
              background: copied ? `${t.accent2}35` : `${t.accent2}15`,
              border: `1px solid ${t.accent2}40`,
              color: copied ? '#fff' : t.accent2,
              borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              transition: 'all 0.3s',
            }}>
            {copied ? '✓ Copied!' : '⎘ Copy JSON'}
          </button>
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.8 }}>
          {renderValue(schema[selected])}
        </div>
      </div>
    </div>
  )
}

// ── Page Detail Panel ─────────────────────────────────────────────────────────
function PageDetail({ jobId, pageUrl, t }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('meta')

  useEffect(() => {
    if (!jobId || !pageUrl) return
    setLoading(true); setData(null)
    fetch(`${API}/page-jobs/${jobId}/page?url=${encodeURIComponent(pageUrl)}`)
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false))
  }, [jobId, pageUrl])

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted }}>Loading page data…</div>
  if (!data) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted }}>Select a page to inspect</div>

  const tabs = [
    { id: 'meta',   label: '🏷 Meta' },
    { id: 'schema', label: '📐 Schema', count: data.schema?.length },
    { id: 'body',   label: '🌳 Body' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page URL header */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, background: `${t.panel}cc`, backdropFilter: 'blur(8px)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{data.url}</span>
          <a href={data.url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: t.accent2, textDecoration: 'none', border: `1px solid ${t.accent2}40`, borderRadius: 4, padding: '2px 7px' }}>↗ Open</a>
          {data.error && <span style={{ fontSize: 10, color: '#f87171', background: '#f8717120', border: '1px solid #f8717140', borderRadius: 4, padding: '2px 7px' }}>⚠ {data.error}</span>}
        </div>
        {data.meta?.title && <div style={{ fontSize: 11, color: t.muted, marginTop: 4 }}>{data.meta.title}</div>}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, padding: '0 16px', flexShrink: 0, background: `${t.panel}80` }}>
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === tb.id ? t.accent : 'transparent'}`, color: tab === tb.id ? t.accent : t.muted, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginBottom: -1, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 5 }}>
            {tb.label}
            {tb.count > 0 && <span style={{ background: `${t.accent}20`, color: t.accent, borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>{tb.count}</span>}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tab === 'meta'   && <MetaViewer   meta={data.meta} statusCode={data.statusCode} t={t} />}
        {tab === 'schema' && <SchemaViewer schema={data.schema} t={t} />}
        {tab === 'body'   && <BodyViewer   body={data.body} t={t} />}
      </div>
    </div>
  )
}

// ── Page Job Card (sidebar) ───────────────────────────────────────────────────
function PageJobCard({ job, selected, onSelect, onStop, onResume, onDelete, t }) {
  // Build a readable domain summary
  const domainLine = job.domains?.length > 0
    ? job.domains.slice(0, 3).join(' · ') + (job.domains.length > 3 ? ` +${job.domains.length - 3}` : '')
    : job.domain;

  return (
    <div onClick={() => onSelect(job.id)} className="job-card-anim"
      style={{ background: selected ? t.jobSelected : t.card, border: `1px solid ${selected ? t.jobSelectedBorder : t.border}`, borderRadius: 12, padding: '12px 13px', marginBottom: 8, cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)', boxShadow: selected ? `0 0 20px ${t.accent}18` : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Main domain/name */}
          <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: t.text, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {job.source === 'paste' || job.source === 'upload' ? domainLine : job.domain}
          </div>
          {/* For paste/upload: show URL count + source badge */}
          {(job.source === 'paste' || job.source === 'upload') && (
            <div style={{ fontSize: 9, color: t.muted, fontFamily: 'monospace', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              {job.source === 'paste' && (
                <span style={{ background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b30', borderRadius: 3, padding: '1px 6px', fontWeight: 700 }}>📋 PASTED URL</span>
              )}
              {job.source === 'upload' && (
                <span style={{ background: '#a78bfa20', color: '#a78bfa', border: '1px solid #a78bfa30', borderRadius: 3, padding: '1px 6px', fontWeight: 700 }}>📁 IMPORTED</span>
              )}
              {job.source === 'domain' && (
                <span style={{ background: `${t.accent2}20`, color: t.accent2, border: `1px solid ${t.accent2}30`, borderRadius: 3, padding: '1px 6px', fontWeight: 700 }}>🌐 DOMAIN</span>
              )}
              {job.totalUrls > 1 && <span style={{ color: t.muted }}>{job.totalUrls} URLs</span>}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Pill status={job.status} />
            {job.source && job.source === 'domain' && job.discoveryMethod && (
              <span style={{ fontSize: 9, color: t.muted, fontFamily: 'monospace', background: t.card, border: `1px solid ${t.border}`, borderRadius: 4, padding: '1px 5px' }}>{job.discoveryMethod}</span>
            )}
          </div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0, marginLeft: 8 }}>
          <Ring value={job.progress} size={48} stroke={4} accent={t.accent2} />
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 9, fontWeight: 700, color: t.accent2, fontFamily: 'monospace' }}>{job.progress}%</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4, marginBottom: 8 }}>
        {[
          { v: job.completedCount, l: 'done',   c: t.accent2 },
          { v: job.totalUrls,      l: 'total',  c: t.muted },
          { v: job.errors || 0,    l: 'errors', c: '#f87171' },
        ].map(s => (
          <div key={s.l} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: s.c }}>{s.v?.toLocaleString?.() ?? s.v}</div>
            <div style={{ fontSize: 9, color: t.muted }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${job.progress}%`, background: `linear-gradient(90deg,${t.accent2},${t.accent3})`, borderRadius: 2, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: t.muted }}>{timeAgo(job.createdAt)}</span>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          {(job.status === 'running' || job.status === 'fetching_sitemap') && (
            <button className="jbtn" style={{ color: '#f59e0b', border: `1px solid rgba(245,158,11,0.3)` }} onClick={() => onStop(job.id)}>⏸</button>
          )}
          {job.status === 'stopped' && (
            <button className="jbtn" style={{ color: t.accent2, border: `1px solid ${t.accent2}40` }} onClick={() => onResume(job.id)}>▶</button>
          )}
          <button className="jbtn" style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }} onClick={() => onDelete(job.id)}>✕</button>
        </div>
      </div>
    </div>
  )
}

function downloadTemplate(fmt) {
  const templates = {
    csv:   { content: 'url\nhttps://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3\n', mime: 'text/csv', name: 'urls_template.csv' },
    txt:   { content: 'https://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3\n', mime: 'text/plain', name: 'urls_template.txt' },
    json:  { content: JSON.stringify(["https://example.com/page1","https://example.com/page2","https://example.com/page3"], null, 2), mime: 'application/json', name: 'urls_template.json' },
    jsonl: { content: '{"url":"https://example.com/page1"}\n{"url":"https://example.com/page2"}\n{"url":"https://example.com/page3"}\n', mime: 'application/x-ndjson', name: 'urls_template.jsonl' },
    xlsx:  null,  // handled separately below
  }
  const tpl = templates[fmt]; if (!tpl) return;
  const blob = new Blob([tpl.content], { type: tpl.mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = tpl.name; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
}

// ── Page Crawler: New Job Panel ───────────────────────────────────────────────
function NewPageJob({ onCreated, t }) {
  const API = `https://carefree-fulfillment-production-1b3f.up.railway.app/api`
  const [mode, setMode] = useState('domain')
  const [domain, setDomain] = useState('')
  const [pasteUrls, setPasteUrls] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [urlCount, setUrlCount] = useState(null)
  const fileRef = useRef()
 
  const iStyle = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 7, padding: '8px 10px', color: t.text, fontSize: 12, outline: 'none', fontFamily: 'monospace', width: '100%', transition: 'border 0.2s' }
 
  // Count URLs in paste box
  const countUrls = (text) => {
    const n = text.split('\n').filter(l => l.trim().startsWith('http')).length
    setUrlCount(n > 0 ? n : null)
  }
 
  const submit = async () => {
    setErr(''); setLoading(true)
    try {
      let res
      if (mode === 'domain') {
        const d = domain.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '').split('/')[0]
        if (!d) { setErr('Enter a domain'); setLoading(false); return }
        res = await fetch(`${API}/page-jobs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain: d }) })
      } else if (mode === 'upload') {
        if (!file) { setErr('Select a file'); setLoading(false); return }
        const fd = new FormData(); fd.append('file', file)
        res = await fetch(`${API}/page-jobs/upload`, { method: 'POST', body: fd })
      } else {
        const urls = pasteUrls.split('\n').map(u => u.trim()).filter(u => u.startsWith('http'))
        if (!urls.length) { setErr('No valid URLs found'); setLoading(false); return }
        res = await fetch(`${API}/page-jobs/urls`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ urls }) })
      }
      const data = await res.json()
      if (data.error) { setErr(data.error); setLoading(false); return }
      setDomain(''); setPasteUrls(''); setFile(null); setUrlCount(null)
      onCreated(data.jobId)
    } catch (e) { setErr(e.message) }
    setLoading(false)
  }
 
  const modeBtn = (id, icon, label) => (
    <button onClick={() => setMode(id)}
      style={{ flex: 1, background: mode === id ? `${t.accent2}20` : t.card, border: `1px solid ${mode === id ? t.accent2 : t.border}`, color: mode === id ? t.accent2 : t.muted, borderRadius: 7, padding: '6px 0', cursor: 'pointer', fontSize: 10, fontWeight: 700, transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      <span>{icon}</span><span>{label}</span>
    </button>
  )
 
  const ACCEPT_FORMATS = '.csv,.txt,.json,.xlsx,.xls,.tsv,.xml,.gz,.zip,.ndjson,.jsonl'
 
  return (
    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${t.border}` }}>
      <div style={{ fontSize: 9, color: t.muted, letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: 8 }}>NEW PAGE CRAWL</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {modeBtn('domain', '🌐', 'Domain')}
        {modeBtn('upload', '📁', 'Import')}
        {modeBtn('paste', '📋', 'Paste')}
      </div>
 
      {mode === 'domain' && (
        <div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={iStyle} placeholder="example.com" value={domain}
              onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
              onFocus={e => e.target.style.borderColor = t.accent2}
              onBlur={e => e.target.style.borderColor = t.border} />
            <button onClick={submit} disabled={loading || !domain.trim()}
              style={{ width: 36, height: 36, borderRadius: 8, border: 'none', flexShrink: 0, background: `linear-gradient(135deg,${t.accent2},${t.accent3})`, color: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer', opacity: loading || !domain.trim() ? 0.4 : 1, transition: 'all 0.2s' }}>
              {loading ? '⟳' : '+'}
            </button>
          </div>
          <div style={{ fontSize: 10, color: t.muted, marginTop: 5 }}>Crawls full site via sitemap or spider</div>
        </div>
      )}
 
      {mode === 'upload' && (
        <div>
        {/* ── Template download bar ── */}
        <div style={{ background: `${t.accent3}10`, border: `1px solid ${t.accent3}25`, borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
          <div style={{ fontSize: 9, color: t.accent3, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>📥 DOWNLOAD IMPORT TEMPLATE</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['csv','txt','json','jsonl'].map(fmt => (
              <button key={fmt} onClick={() => downloadTemplate(fmt)}
                style={{ background: t.card, border: `1px solid ${t.border}`, color: t.accent3, borderRadius: 5, padding: '3px 9px', cursor: 'pointer', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent3; e.currentTarget.style.background = `${t.accent3}15` }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.background = t.card }}>
                ↓ {fmt.toUpperCase()}
              </button>
            ))}
          </div>
          </div>
        <div>
          {/* Format chips */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {['CSV','TSV','TXT','JSON','JSONL','XLSX','XLS','XML'].map(fmt => (
              <span key={fmt} style={{ fontSize: 9, color: t.accent3, background: `${t.accent3}15`, border: `1px solid ${t.accent3}30`, borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace', fontWeight: 700 }}>{fmt}</span>
            ))}
          </div>
          <div onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${file ? t.accent2 : t.border}`, borderRadius: 8, padding: '14px 10px', textAlign: 'center', cursor: 'pointer', color: file ? t.accent2 : t.muted, fontSize: 11, transition: 'all 0.2s', marginBottom: 8 }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f) }}
            onMouseEnter={e => e.currentTarget.style.borderColor = t.accent2}
            onMouseLeave={e => e.currentTarget.style.borderColor = file ? t.accent2 : t.border}>
            {file ? (
              <div>
                <div style={{ fontSize: 14, marginBottom: 4 }}>📄 {file.name}</div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 20, marginBottom: 4 }}>📁</div>
                <div>Click or drag & drop</div>
                <div style={{ fontSize: 9, opacity: 0.6, marginTop: 2 }}>CSV · TSV · TXT · JSON · JSONL · XLSX · XLS · XML</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept={ACCEPT_FORMATS} style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0])} />
          <button onClick={submit} disabled={loading || !file}
            style={{ width: '100%', background: `linear-gradient(135deg,${t.accent2},${t.accent3})`, border: 'none', borderRadius: 8, padding: '8px 0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: loading || !file ? 0.4 : 1, transition: 'all 0.2s' }}>
            {loading ? '⟳ Parsing…' : '↑ Import & Crawl'}
          </button>
          <div style={{ fontSize: 10, color: t.muted, marginTop: 5, textAlign: 'center' }}>
            URLs auto-extracted from any column/field
          </div>
        </div>
        </div>
      )}
 
      {mode === 'paste' && (
        <div>
          <div style={{ position: 'relative' }}>
            <textarea style={{ ...iStyle, height: 90, resize: 'vertical', lineHeight: 1.6 }}
              placeholder={'https://example.com/page1\nhttps://example.com/page2\n…'}
              value={pasteUrls}
              onChange={e => { setPasteUrls(e.target.value); countUrls(e.target.value) }} />
            {urlCount && (
              <span style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 9, color: t.accent2, background: t.panel, border: `1px solid ${t.accent2}40`, borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace' }}>
                {urlCount} URLs
              </span>
            )}
          </div>
          <button onClick={submit} disabled={loading || !pasteUrls.trim()}
            style={{ width: '100%', background: `linear-gradient(135deg,${t.accent2},${t.accent3})`, border: 'none', borderRadius: 8, padding: '8px 0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: loading || !pasteUrls.trim() ? 0.4 : 1, marginTop: 6, transition: 'all 0.2s' }}>
            {loading ? '⟳ Starting…' : `Crawl ${urlCount ? urlCount + ' URLs' : 'Pasted URLs'}`}
          </button>
        </div>
      )}
 
      {err && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6, padding: '4px 8px', background: '#f8717115', borderRadius: 5 }}>⚠ {err}</div>}
    </div>
  )
}


// ── Page Crawler: Page Index List ─────────────────────────────────────────────
function PageIndexList({ job, selectedUrl, onSelect, t }) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all') // 'all' | 'ok' | 'error' | 'schema'
  const pages = Object.values(job?.pageIndex || {})

  let filtered = pages
  if (search) filtered = filtered.filter(p => p.url.toLowerCase().includes(search.toLowerCase()) || (p.title || '').toLowerCase().includes(search.toLowerCase()))
  if (filter === 'ok') filtered = filtered.filter(p => p.statusCode >= 200 && p.statusCode < 300 && !p.error)
  if (filter === 'error') filtered = filtered.filter(p => p.error || (p.statusCode && p.statusCode >= 400))
  if (filter === 'schema') filtered = filtered.filter(p => p.schemaCount > 0)

  const iStyle = { background: t.card, border: `1px solid ${t.border}`, borderRadius: 7, padding: '6px 10px', color: t.text, fontSize: 11, outline: 'none', fontFamily: 'monospace' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, background: `${t.panel}aa` }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: t.muted, fontSize: 12 }}>⌕</span>
            <input style={{ ...iStyle, paddingLeft: 26, width: '100%' }} placeholder="Search pages…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['all', 'All'], ['ok', '✓ OK'], ['error', '⚠ Errors'], ['schema', '📐 Schema']].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              style={{ background: filter === id ? `${t.accent2}20` : t.card, border: `1px solid ${filter === id ? t.accent2 : t.border}`, color: filter === id ? t.accent2 : t.muted, borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>
              {label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 10, color: t.muted, alignSelf: 'center', fontFamily: 'monospace' }}>{filtered.length}</span>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: t.muted, fontSize: 12 }}>
            {job?.status === 'fetching_sitemap' || job?.status === 'running' ? '⏳ Crawling in progress…' : 'No pages match'}
          </div>
        ) : filtered.map(page => {
          const isSelected = page.url === selectedUrl
          const hasError = page.error || (page.statusCode && page.statusCode >= 400)
          const sc = page.statusCode
          const statusColor = sc >= 200 && sc < 300 ? '#34d399' : sc >= 300 && sc < 400 ? '#f59e0b' : '#f87171'
          return (
            <div key={page.url} onClick={() => onSelect(page.url)}
              style={{ padding: '9px 16px', borderBottom: `1px solid ${t.border}20`, cursor: 'pointer', background: isSelected ? `${t.accent2}12` : 'transparent', borderLeft: `3px solid ${isSelected ? t.accent2 : 'transparent'}`, transition: 'all 0.15s' }}
              onMouseEnter={e => !isSelected && (e.currentTarget.style.background = `${t.accent2}08`)}
              onMouseLeave={e => !isSelected && (e.currentTarget.style.background = 'transparent')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                {sc && <span style={{ fontSize: 9, color: statusColor, fontFamily: 'monospace', fontWeight: 700 }}>{sc}</span>}
                {page.schemaCount > 0 && <span style={{ fontSize: 9, color: t.accent3, background: `${t.accent3}15`, border: `1px solid ${t.accent3}30`, borderRadius: 3, padding: '0 4px' }}>S×{page.schemaCount}</span>}
                {hasError && <span style={{ fontSize: 9, color: '#f87171' }}>⚠</span>}
              </div>
              <div style={{ fontSize: 11, color: isSelected ? t.accent2 : t.text, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                {page.url.replace(/^https?:\/\/[^/]+/, '') || '/'}
              </div>
              {page.title && <div style={{ fontSize: 10, color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.title}</div>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Page Crawler: Export Bar ──────────────────────────────────────────────────
function ExportBar({ jobId, domain, t }) {
  const [open, setOpen] = useState(false)
  
  const exports = [
    { href: 'export/csv',        label: '↓ CSV (index)',    color: t.accent,   desc: 'Basic index' },
    { href: 'export/csv-full',   label: '↓ CSV (full)',     color: t.accent,   desc: 'All meta fields' },
    { href: 'export/xlsx',       label: '↓ Excel (index)',  color: '#34d399',  desc: 'Excel spreadsheet' },
    { href: 'export/xlsx-full',  label: '↓ Excel (full)',   color: '#34d399',  desc: 'All fields in Excel' },
    { href: 'export/json',       label: '↓ JSON (full)',    color: t.accent3,  desc: 'Complete JSON data' },
    { href: 'export/json-index', label: '↓ JSON (index)',   color: t.accent3,  desc: 'Index JSON' },
    { href: 'export/txt',        label: '↓ TXT (content)',  color: '#f472b6',  desc: 'Formatted text' },
  ]
 
  const API = `https://carefree-fulfillment-production-1b3f.up.railway.app/api`
 
  return (
    <div style={{ display: 'flex', gap: 6, padding: '8px 16px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, background: `${t.panel}cc`, backdropFilter: 'blur(8px)', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, color: t.muted, fontFamily: 'monospace' }}>EXPORT</span>
      {exports.map(({ href, label, color }) => (
        <a key={href} href={`${API}/page-jobs/${jobId}/${href}`}
          style={{ background: `${color}15`, border: `1px solid ${color}40`, color, borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, textDecoration: 'none', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.background = `${color}28`}
          onMouseLeave={e => e.currentTarget.style.background = `${color}15`}>
          {label}
        </a>
      ))}
    </div>
  )
}
 
// ── Page Crawler: Main Panel ──────────────────────────────────────────────────
// Drop this in to REPLACE the existing PageCrawlerMain function in App.js
function PageCrawlerMain({ pageJobs, t, selectedJobId, onSelectJob }) {
  const [selectedUrl, setSelectedUrl] = useState(null)
  const [fullJob, setFullJob] = useState(null)
  const [loadingJob, setLoadingJob] = useState(false)

  useEffect(() => {
    if (!selectedJobId) { setFullJob(null); setSelectedUrl(null); return }

    const load = async () => {
      try {
        const r = await fetch(`${API}/page-jobs/${selectedJobId}`)
        const data = await r.json()
        setFullJob(data)
        if (data.pageIndex?.length === 1 && !selectedUrl) {
          setSelectedUrl(data.pageIndex[0].url)
        }
      } catch {}
      setLoadingJob(false)
    }

    setLoadingJob(true)
    load()

    const interval = setInterval(async () => {
      const summary = pageJobs.find(j => j.id === selectedJobId)
      if (!summary) return
      await load()
      if (summary.status === 'completed' || summary.status === 'stopped' || summary.status === 'failed') {
        clearInterval(interval)
      }
    }, 2500)

    return () => clearInterval(interval)
  }, [selectedJobId])

  // Reset selected URL when job changes
  useEffect(() => {
    setSelectedUrl(null)
  }, [selectedJobId])

  useEffect(() => {
    if (!selectedJobId) return
    const summary = pageJobs.find(j => j.id === selectedJobId)
    if (summary?.status === 'running' || summary?.status === 'fetching_sitemap') {
      fetch(`${API}/page-jobs/${selectedJobId}`)
        .then(r => r.json())
        .then(data => {
          setFullJob(data)
          if (data.pageIndex?.length === 1 && !selectedUrl) {
            setSelectedUrl(data.pageIndex[0].url)
          }
        })
        .catch(() => {})
    }
  }, [pageJobs, selectedJobId])

  const selectedSummary = pageJobs.find(j => j.id === selectedJobId)
  const mergedJob = fullJob
    ? { ...fullJob, ...selectedSummary, pageIndex: fullJob.pageIndex || [] }
    : null

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left panel: page index list ── */}
      <div style={{ width: 300, minWidth: 300, borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mergedJob ? (
          <>
            {/* Job header */}
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, background: `${t.panel}cc` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{mergedJob.domain}</span>
                <Pill status={mergedJob.status} />
              </div>
              <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
                <div style={{ height: '100%', width: `${mergedJob.progress}%`, background: `linear-gradient(90deg,${t.accent2},${t.accent3})`, borderRadius: 2, transition: 'width 0.5s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: t.muted, fontFamily: 'monospace' }}>
                <span><b style={{ color: t.accent2 }}>{mergedJob.completedCount}</b> / {mergedJob.totalUrls} pages</span>
                <span style={{ color: t.accent3 }}>{mergedJob.progress}%</span>
              </div>
            </div>

            {loadingJob && (!mergedJob.pageIndex || mergedJob.pageIndex.length === 0) ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted, fontSize: 12 }}>
                Loading pages…
              </div>
            ) : (
              <PageIndexList
                job={{ ...mergedJob, pageIndex: Object.fromEntries((mergedJob.pageIndex || []).map(p => [p.url, p])) }}
                selectedUrl={selectedUrl}
                onSelect={setSelectedUrl}
                t={t}
              />
            )}
          </>
        ) : (
          /* No job selected yet */
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: t.muted, padding: 20 }}>
              <div style={{ fontSize: 32, opacity: 0.15, marginBottom: 8 }}>←</div>
              <div style={{ fontSize: 12 }}>Select a job from the sidebar</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel: page detail ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mergedJob && selectedUrl ? (
          <>
            <ExportBar jobId={mergedJob.id} domain={mergedJob.domain} t={t} />
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <PageDetail jobId={mergedJob.id} pageUrl={selectedUrl} t={t} key={selectedUrl} />
            </div>
          </>
        ) : mergedJob ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: t.muted }}>
              <div style={{ fontSize: 40, opacity: 0.15, marginBottom: 10 }}>←</div>
              <div style={{ fontSize: 13 }}>Select a page from the list</div>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>Meta · Schema · Body content</div>
              {(mergedJob.status === 'running' || mergedJob.status === 'fetching_sitemap') && (
                <div style={{ marginTop: 14, fontSize: 11, color: t.accent2 }}>⏳ Crawl in progress…</div>
              )}
              {mergedJob.status !== 'running' && mergedJob.status !== 'fetching_sitemap' && mergedJob.pageIndex?.length === 0 && (
                <div style={{ marginTop: 14, fontSize: 11, color: '#f87171' }}>⚠ No pages crawled</div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', background: `${t.card}cc`, backdropFilter: 'blur(12px)', border: `1px solid ${t.border}`, borderRadius: 20, padding: '40px 60px' }}>
              <div style={{ fontSize: 44, opacity: 0.2, marginBottom: 12 }}>📄</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 8 }}>Page Data Crawler</div>
              <div style={{ fontSize: 12, color: t.muted, maxWidth: 280, lineHeight: 1.6 }}>
                Crawl entire websites and inspect metadata, schema markup, and body content for each page.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
                {['🏷 Meta tags', '📐 Schema.org', '🌳 Tag tree', '📄 Raw HTML', '↓ Export'].map(f => (
                  <span key={f} style={{ background: `${t.accent2}15`, border: `1px solid ${t.accent2}30`, color: t.accent2, borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// MAIN APP
// ════════════════════════════════════════════════════════════════
export default function App() {
  const [themeKey, setThemeKey] = useState(() => localStorage.getItem('crawlerTheme') || 'neon')
  const [pendingTheme, setPendingTheme] = useState(null)
  const t = THEMES[themeKey] || THEMES.neon

  // Top-level mode: 'images' | 'pages'
  const [appMode, setAppMode] = useState('images')

  // Image crawler state
  const [jobs, setJobs] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [tab, setTab] = useState('feed')
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [connErr, setConnErr] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [selectedPageJobId, setSelectedPageJobId] = useState(null)
  // Page crawler state
  const [pageJobs, setPageJobs] = useState([])

  const changeTheme = (key) => { if (key === themeKey || pendingTheme) return; setPendingTheme(key) }
  const handleTransitionDone = () => { setThemeKey(pendingTheme); localStorage.setItem('crawlerTheme', pendingTheme); setPendingTheme(null) }

  const fetchJobs = useCallback(async () => {
    try { const r = await fetch(`${API}/jobs`); setJobs(await r.json()); setConnErr(false) }
    catch { setConnErr(true) }
  }, [])

  const fetchPageJobs = useCallback(async () => {
    try { const r = await fetch(`${API}/page-jobs`); setPageJobs(await r.json()) }
    catch {}
  }, [])

  useEffect(() => {
    fetchJobs(); fetchPageJobs()
    const p = setInterval(fetchJobs, 2000)
    const pp = setInterval(fetchPageJobs, 2500)
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => { clearInterval(p); clearInterval(pp); clearInterval(tick) }
  }, [fetchJobs, fetchPageJobs])

  const addJob = async () => {
    const d = newDomain.trim(); if (!d) return
    setAdding(true)
    try {
      const r = await fetch(`${API}/jobs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain: d }) })
      const data = await r.json()
      setNewDomain(''); setSelectedId(data.jobId); setTab('feed'); fetchJobs()
    } catch {}
    setAdding(false)
  }

  const stopJob = async id => { await fetch(`${API}/jobs/${id}/stop`, { method: 'POST' }); fetchJobs() }
  const resumeJob = async id => { await fetch(`${API}/jobs/${id}/resume`, { method: 'POST' }); fetchJobs() }
  const deleteJob = async id => { await fetch(`${API}/jobs/${id}`, { method: 'DELETE' }); if (selectedId === id) setSelectedId(null); fetchJobs() }

  const selected = jobs.find(j => j.id === selectedId)
  const activeImgCount = jobs.filter(j => j.status === 'running' || j.status === 'fetching_sitemap').length
  const activePageCount = pageJobs.filter(j => j.status === 'running' || j.status === 'fetching_sitemap').length

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: t.bg, color: t.text, fontFamily: "'Inter',system-ui,sans-serif", fontSize: 13, transition: 'background 0.5s ease, color 0.3s ease' }}>
      <ThemeCanvas themeKey={themeKey} />
      {pendingTheme && <ThemeTransition themeKey={pendingTheme} onDone={handleTransitionDone} />}

      {/* ── Sidebar ──────────────────────────────────────────── */}
      <div style={{ width: 295, minWidth: 295, background: `${t.panel}f0`, backdropFilter: 'blur(12px)', borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 10 }}>
        {/* Brand */}
        <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: `linear-gradient(135deg,${t.accent}40,${t.accent2}40)`, border: `1px solid ${t.accent}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>⚡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', color: t.text, background: `linear-gradient(135deg,${t.accent},${t.accent2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>WebCrawler</div>
            <div style={{ fontSize: 9, color: t.muted, letterSpacing: '0.1em', fontFamily: 'monospace' }}>ASSET + PAGE SCANNER</div>
          </div>
          {(activeImgCount + activePageCount) > 0 && (
            <div style={{ background: `${t.accent}18`, color: t.accent, border: `1px solid ${t.accent}35`, borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, animation: 'blink 1.5s ease-in-out infinite' }}>{activeImgCount + activePageCount} live</div>
          )}
        </div>

        <ThemePicker current={themeKey} onChange={changeTheme} t={t} />

        {/* Mode switcher */}
        <div style={{ display: 'flex', padding: '10px 14px', gap: 6, borderBottom: `1px solid ${t.border}` }}>
          {[['images', '🖼 Images'], ['pages', '📄 Pages']].map(([mode, label]) => (
            <button key={mode} onClick={() => setAppMode(mode)}
              style={{ flex: 1, background: appMode === mode ? `${t.accent}20` : t.card, border: `1px solid ${appMode === mode ? t.accent : t.border}`, color: appMode === mode ? t.accent : t.muted, borderRadius: 8, padding: '7px 0', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'all 0.2s' }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Image Crawler Sidebar Content ── */}
        {appMode === 'images' && (
          <>
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 9, color: t.muted, letterSpacing: '0.12em', fontFamily: 'monospace', marginBottom: 8 }}>NEW IMAGE CRAWL</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ flex: 1, background: t.card, border: `1px solid ${t.border}`, borderRadius: 7, padding: '8px 10px', color: t.text, fontSize: 12, outline: 'none', fontFamily: 'monospace', transition: 'border 0.2s' }}
                  placeholder="example.com" value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addJob()}
                  onFocus={e => e.target.style.borderColor = t.accent}
                  onBlur={e => e.target.style.borderColor = t.border} />
                <button onClick={addJob} disabled={adding || !newDomain.trim()}
                  style={{ width: 36, height: 36, borderRadius: 8, border: 'none', flexShrink: 0, background: `linear-gradient(135deg,${t.accent},${t.accent2})`, color: '#fff', fontSize: 22, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: adding || !newDomain.trim() ? 0.4 : 1, transition: 'all 0.2s' }}>
                  {adding ? '⟳' : '+'}
                </button>
              </div>
              {connErr && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>⚠ Backend not reachable on :3001</div>}
              <div style={{ fontSize: 10, color: t.muted, marginTop: 5 }}>No limit · resumes on restart · 10× parallel</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
              {jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                  <div style={{ fontSize: 36, opacity: 0.12, marginBottom: 8 }}>🌐</div>
                  <div style={{ fontSize: 12, color: t.muted }}>No image crawl jobs yet</div>
                </div>
              ) : jobs.map(job => (
                <JobCard key={job.id} job={job} selected={job.id === selectedId} t={t}
                  onSelect={id => { setSelectedId(id); setTab('feed') }}
                  onStop={stopJob} onResume={resumeJob} onDelete={deleteJob} />
              ))}
            </div>
            <div style={{ padding: '8px 16px', borderTop: `1px solid ${t.border}`, fontSize: 10, color: t.muted, display: 'flex', gap: 6, fontFamily: 'monospace', background: `${t.card}80` }}>
              <span>{jobs.length} jobs</span><span>·</span>
              <span style={{ color: t.accent }}>{jobs.reduce((s, j) => s + (j.imageCount || 0), 0).toLocaleString()} images</span>
            </div>
          </>
        )}

        {/* ── Page Crawler Sidebar Content ── */}
        {appMode === 'pages' && (
          <>
            <NewPageJob onCreated={id => { fetchPageJobs() }} t={t} />
            <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
              {pageJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                  <div style={{ fontSize: 36, opacity: 0.12, marginBottom: 8 }}>📄</div>
                  <div style={{ fontSize: 12, color: t.muted }}>No page crawl jobs yet</div>
                </div>
              ) : pageJobs.map(job => (
                <PageJobCard key={job.id} job={job} selected={job.id === selectedPageJobId} t={t}
                  onSelect={id => setSelectedPageJobId(id)}
                  onStop={async id => { await fetch(`${API}/page-jobs/${id}/stop`, { method: 'POST' }); fetchPageJobs() }}
                  onResume={async id => { await fetch(`${API}/page-jobs/${id}/resume`, { method: 'POST' }); fetchPageJobs() }}
                  onDelete={async id => { await fetch(`${API}/page-jobs/${id}`, { method: 'DELETE' }); fetchPageJobs() }} />
              ))}
            </div>
            <div style={{ padding: '8px 16px', borderTop: `1px solid ${t.border}`, fontSize: 10, color: t.muted, display: 'flex', gap: 6, fontFamily: 'monospace', background: `${t.card}80` }}>
              <span>{pageJobs.length} jobs</span><span>·</span>
              <span style={{ color: t.accent2 }}>{pageJobs.reduce((s, j) => s + (j.completedCount || 0), 0).toLocaleString()} pages</span>
            </div>
          </>
        )}
      </div>

      {/* ── Main Panel ───────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 5 }}>

        {/* ── Image Crawler Main ── */}
        {appMode === 'images' && (
          selected ? (
            <>
              <div style={{ padding: '14px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: `${t.panel}cc`, backdropFilter: 'blur(12px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 800, background: `linear-gradient(135deg,${t.accent},${t.accent2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{selected.domain}</div>
                  <Pill status={selected.status} />
                  {selected.discoveryMethod && <span style={{ fontSize: 10, color: t.muted, background: t.card, border: `1px solid ${t.border}`, borderRadius: 4, padding: '2px 7px', fontFamily: 'monospace' }}>{selected.discoveryMethod}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {selected.startedAt && <span style={{ fontSize: 11, color: t.muted, fontFamily: 'monospace' }}>⏱ {formatTime(now - selected.startedAt)}</span>}
                  {selected.imageCount > 0 && (
                    <a href={`${API}/jobs/${selected.id}/csv`}
                      style={{ background: `${t.accent}15`, border: `1px solid ${t.accent}40`, color: t.accent, borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = `${t.accent}25`}
                      onMouseLeave={e => e.currentTarget.style.background = `${t.accent}15`}>
                      ↓ Export CSV ({selected.imageCount.toLocaleString()} rows)
                    </a>
                  )}
                </div>
              </div>
              <div style={{ padding: '10px 24px', flexShrink: 0, background: `${t.panel}aa`, backdropFilter: 'blur(8px)', borderBottom: `1px solid ${t.border}` }}>
                <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${selected.progress}%`, background: t.progress, borderRadius: 3, transition: 'width 0.6s ease', boxShadow: `0 0 14px ${t.accent}50` }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.muted, fontFamily: 'monospace', marginTop: 5 }}>
                  <span><b style={{ color: t.accent }}>{selected.completedCount.toLocaleString()}</b> / {selected.totalUrls.toLocaleString()} pages</span>
                  <span><b style={{ color: t.accent2 }}>{selected.imageCount.toLocaleString()}</b> images found</span>
                  <span>{selected.errors > 0 && <span style={{ color: '#f87171' }}>⚠ {selected.errors} err · </span>}{selected.progress}%</span>
                </div>
              </div>
              <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, padding: '0 24px', flexShrink: 0, background: `${t.panel}aa`, backdropFilter: 'blur(8px)' }}>
                {[{ id: 'feed', l: '⚡ Live Feed' }, { id: 'stats', l: '📊 Stats' }, { id: 'saved', l: '💾 Saved Data' }].map(tb => (
                  <button key={tb.id} onClick={() => setTab(tb.id)}
                    style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === tb.id ? t.tabActive : 'transparent'}`, color: tab === tb.id ? t.tabActive : t.muted, padding: '10px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginBottom: -1, transition: 'all 0.2s' }}>
                    {tb.l}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {tab === 'feed'  && <LiveFeed   jobId={selected.id} jobStatus={selected.status} t={t} key={selected.id + 'f'} />}
                {tab === 'stats' && <StatsPanel  jobId={selected.id} t={t} key={selected.id + 's'} />}
                {tab === 'saved' && <SavedData   jobs={jobs} t={t} onSelectJob={id => { setSelectedId(id); setTab('feed') }} />}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: `${t.card}cc`, backdropFilter: 'blur(12px)', border: `1px solid ${t.border}`, borderRadius: 24, padding: '50px 70px', textAlign: 'center' }}>
                <div style={{ fontSize: 52, marginBottom: 16, opacity: 0.3 }}>⚡</div>
                <div style={{ fontSize: 18, fontWeight: 700, background: `linear-gradient(135deg,${t.accent},${t.accent2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 8 }}>Select a job to view data</div>
                <div style={{ fontSize: 13, color: t.muted, marginBottom: 20 }}>Or start a new crawl from the sidebar</div>
                {jobs.filter(j => j.imageCount > 0).length > 0 && (
                  <button onClick={() => { setTab('saved'); setSelectedId(jobs.find(j => j.imageCount > 0)?.id) }}
                    style={{ background: `${t.accent}15`, border: `1px solid ${t.accent}40`, color: t.accent, borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    💾 View Saved Data →
                  </button>
                )}
              </div>
            </div>
          )
        )}

        {/* ── Page Crawler Main ── */}
        {appMode === 'pages' && (
           <PageCrawlerMain
           pageJobs={pageJobs}
           t={t}
           selectedJobId={selectedPageJobId}
           onSelectJob={setSelectedPageJobId}
           key="page-main"
         />
        )}
      </div>
    </div>
  )
}